/* B6 — Workstage 3 (Free Design Onboarding) admin modules.
 *
 * Renders the five-module vertical stack (§5) inside the canonical
 * Project Workspace accordion when WS3 is Active.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import MarkSentExternallyControl from "./MarkSentExternallyControl";
import RecordExternalExecutionModal, {
    EXT_METHOD_LABELS,
} from "./RecordExternalExecutionModal";

const CopyBtn = ({ value, testid }) => (
    <button type="button"
        onClick={() => navigator.clipboard.writeText(value)}
        className="text-[11px] underline text-[#666] hover:text-charcoal"
        data-testid={testid}>Copy</button>
);

export function WS3ActiveModules({ projectId, mode = "active", onCompleted }) {
    const [ws3, setWs3] = useState(null);
    const [blockers, setBlockers] = useState([]);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [recExtOpen, setRecExtOpen] = useState(false);
    const opId = () =>
        `ws3-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const load = async () => {
        try {
            const r = await api.get(`/admin/projects/${projectId}/ws3`);
            setWs3(r.data);
            const c = await api.get(
                `/admin/projects/${projectId}/ws3/completion`);
            setBlockers(c.data.blockers || []);
        } catch (e) {
            const d = e?.response?.data?.detail;
            setErr(typeof d === "string" ? d : (d?.detail || d?.error
                || "Failed to load WS3"));
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
    const complete = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/projects/${projectId}/ws3/complete`,
                { create_op_id: opId() });
            toast.success("WS3 completed");
            onCompleted && onCompleted();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (Array.isArray(d?.blockers)) {
                toast.error(d.blockers.map(b => b.detail).join(" · "));
            } else {
                toast.error(d?.error || "Failed");
            }
            load();
        } finally { setBusy(false); }
    };
    if (err) return <div className="text-terracotta text-[13px]" data-testid="ws3-error">{err}</div>;
    if (!ws3) return <div className="text-[#666] text-[13px]" data-testid="ws3-loading">Loading Free Design Onboarding…</div>;
    const st = ws3.onboarding_status || {};
    const agr = ws3.agreement;
    const statusRows = [
        ["Client details confirmed", st.client_details_confirmed],
        ["Agreement executed", st.agreement_executed],
        ["Studio G4 countersignature", st.lrs_countersignature],
        ["Essential onboarding information complete",
            st.essential_information_complete],
    ];
    // D5 Slice B — coherent mode propagation.
    const readOnly = mode !== "active";
    return (
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws3-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws3-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-3" data-testid="ws3-page">
            {/* Module 1 — Onboarding Status */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws3-module-onboarding-status">
                <h3 className="font-manrope font-semibold text-[14px]">
                    1. Onboarding Status
                </h3>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-[13px]"
                    data-testid="ws3-status-list">
                    {statusRows.map(([label, done]) => (
                        <li key={label} className="flex items-center gap-2">
                            <span aria-hidden
                                className={`w-3 h-3 rounded-full border ${done ? "bg-charcoal border-charcoal" : "bg-white border-[#C7BEB0]"}`} />
                            <span className={done ? "text-charcoal" : "text-[#666]"}>
                                {label}
                            </span>
                        </li>
                    ))}
                </ul>
            </section>
            {/* Module 2 — Free Design Agreement */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws3-module-agreement">
                <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-manrope font-semibold text-[14px]">
                        2. Free Design Agreement
                    </h3>
                    {agr && (
                        <span className="text-[11px] uppercase tracking-wide text-[#666] font-manrope font-semibold"
                              data-testid="ws3-agreement-status">
                            {agr.status}
                        </span>
                    )}
                </div>
                {!agr && (
                    <div className="text-[13px] text-[#666]">
                        No agreement on file yet.
                    </div>
                )}
                {agr && (
                    <div className="flex flex-col gap-2 text-[13px]">
                        <div className="flex items-center gap-2">
                            <span className="text-[#666] text-[11px] uppercase">Reference</span>
                            <span data-testid="ws3-agreement-reference"
                                  className="font-mono">{agr.agreement_reference}</span>
                            <CopyBtn value={agr.agreement_reference}
                                testid="ws3-agreement-copy-ref" />
                            <span className="ml-auto text-[11px] text-[#666]">
                                Version {agr.agreement_version}
                            </span>
                        </div>
                        <div className="border border-[#E5E2DC] rounded-md p-2 flex flex-col gap-1"
                             data-testid="ws3-signer-list">
                            <div className="text-[11px] uppercase text-[#666]">
                                Signers ({agr.signed_by_all_clients
                                    ? "all signed"
                                    : `${agr.outstanding_signer_count} outstanding`})
                            </div>
                            {(agr.signers || []).map(s => (
                                <div key={s.id}
                                    className="flex items-center gap-2"
                                    data-testid={`ws3-signer-${s.id}`}>
                                    <span aria-hidden
                                        className={`w-2 h-2 rounded-full ${s.state === "signed" ? "bg-charcoal" : "bg-[#C7BEB0]"}`} />
                                    <span className="text-charcoal">
                                        {s.name}
                                    </span>
                                    <span className="text-[11px] text-[#666]">
                                        {s.email}
                                    </span>
                                    <span className="ml-auto text-[11px] uppercase text-[#666]">
                                        {s.state}
                                    </span>
                                </div>
                            ))}
                        </div>
                        {agr.countersignature && (
                            <div className="text-[12px] text-[#666]"
                                 data-testid="ws3-countersignature">
                                Studio G4 countersigned by
                                <span className="ml-1 text-charcoal">
                                    {agr.countersignature.signatory_name}
                                </span>
                                <span className="ml-1">on</span>
                                <span className="ml-1">
                                    {String(agr.countersignature.signed_at).slice(0, 10)}
                                </span>
                            </div>
                        )}
                        {(agr.executed_documents || []).length > 0 && (
                            <div className="text-[12px] flex flex-col gap-1"
                                 data-testid="ws3-executed-documents">
                                <span className="text-[#666]">
                                    Executed document{agr.executed_documents.length > 1 ? "s" : ""}:
                                </span>
                                {agr.executed_documents.map((ed, idx) => (
                                    <div key={ed.file_id || idx}
                                         className="flex items-center flex-wrap gap-2"
                                         data-testid={`ws3-executed-doc-${idx}`}>
                                        <a href={`${API}/admin/files/${ed.file_id}/download`}
                                           data-testid={`ws3-executed-doc-link-${idx}`}
                                           className="underline text-[#A8543F] font-mono">
                                            {ed.filename}
                                        </a>
                                        <span className="text-[11px] text-[#999]">
                                            hash {String(ed.content_hash || "").slice(0, 12)}…
                                        </span>
                                        {ed.source === "external_upload" && (
                                            <span className="inline-flex rounded-full bg-[#F5F3EE] px-2 py-0.5 text-[10px] uppercase tracking-wide text-[#666]"
                                                  data-testid={`ws3-executed-doc-source-external-${idx}`}>
                                                External upload
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {(agr.external_signers || []).length > 0 && (
                            <div className="border border-[#E5E2DC] rounded-md p-2"
                                 data-testid="ws3-ext-signers">
                                <div className="text-[11px] uppercase text-[#666] mb-1">
                                    External signer evidence
                                </div>
                                <ul className="list-disc ml-5 text-[11px] flex flex-col gap-0.5">
                                    {agr.external_signers.map(s => (
                                        <li key={s.id}
                                            data-testid={`ws3-ext-signer-${s.id}`}>
                                            {s.signer_name} — {s.signer_role}
                                            {" · signed "}{s.signed_on}
                                            {" · "}
                                            <span className="inline-flex rounded-full bg-[#F5F3EE] px-2 py-0.5 text-[10px]"
                                                  data-testid={`ws3-ext-signer-${s.id}-method`}>
                                                {EXT_METHOD_LABELS[s.signature_method] || s.signature_method}
                                            </span>
                                            {s.external_envelope_ref && (
                                                <span className="ml-1 text-[#666]">
                                                    · ref {s.external_envelope_ref}
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                    {!agr && (
                        <>
                            <button type="button"
                                disabled
                                aria-disabled="true"
                                title="Coming soon — in-app creation with e-signature. Use 'Upload Signed Agreement' for externally-signed documents."
                                data-testid="ws3-agreement-create-disabled"
                                className="text-[13px] px-3 py-1.5 border border-[#E5E2DC] rounded-md text-[#999] bg-[#F5F3EE] cursor-not-allowed">
                                Create Agreement
                            </button>
                            <button type="button"
                                onClick={() => setRecExtOpen(true)}
                                disabled={busy}
                                data-testid="ws3-agreement-upload-signed"
                                className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                                Upload Signed Agreement
                            </button>
                        </>
                    )}
                    {agr && ["draft", "ready_to_issue", "issued",
                              "partially_signed", "fully_signed",
                              "pending_upload"].includes(agr.status) && (
                        <button type="button"
                            onClick={() => setRecExtOpen(true)}
                            disabled={busy}
                            data-testid="ws3-agreement-record-external-execution"
                            className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                            Upload Signed Agreement
                        </button>
                    )}
                </div>
            </section>
            {/* Module 3 — Onboarding Information */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws3-module-onboarding-information">
                <h3 className="font-manrope font-semibold text-[14px]">
                    3. Onboarding Information
                </h3>
                <div className="text-[13px] text-[#666]">
                    Reuses the global Information Request workflow with
                    Linked Workstage = 3.
                </div>
                {ws3.essential_info && (
                    <div className="text-[12px]" data-testid="ws3-info-summary">
                        Essential items:
                        <span className="ml-1 font-mono">
                            {ws3.essential_info.total - ws3.essential_info.outstanding}
                            /{ws3.essential_info.total}
                        </span>
                        <span className="ml-1 text-[#666]">
                            received
                        </span>
                    </div>
                )}
            </section>
            {/* Module 4 (Design Brief) deleted in Phase 2. */}
            {/* Module 5 — Admin Decision / Workstage Completion */}
            <section className="bg-white border border-[#E5E2DC] rounded-lg p-5 flex flex-col gap-3"
                     data-testid="ws3-module-decision">
                <h3 className="font-manrope font-semibold text-[14px]">
                    5. Admin Decision / Workstage Completion
                </h3>
                {blockers.length === 0 ? (
                    <div className="text-[13px] text-charcoal"
                         data-testid="ws3-completion-ready">
                        All completion prerequisites satisfied.
                    </div>
                ) : (
                    <ul className="text-[12.5px] list-disc pl-5 text-[#666]"
                        data-testid="ws3-completion-blockers">
                        {blockers.map(b => (
                            <li key={b.code}
                                data-testid={`ws3-blocker-${b.code}`}>
                                {b.detail}
                            </li>
                        ))}
                    </ul>
                )}
                {blockers.some(b => b.code === "onboarding_complete_communication_outstanding") && (
                    <MarkSentExternallyControl
                        testid="ws3-mark-sent-externally"
                        endpointUrl={`${API}/admin/projects/${projectId}/ws3/comms/mark-sent-externally`}
                        buildBody={({ sent_on, notes, op_id }) => ({ sent_on, notes, op_id })}
                        alreadySatisfied={!blockers.some(b => b.code === "onboarding_complete_communication_outstanding")}
                        onSuccess={load} />
                )}
                <div className="flex justify-end">
                    <button onClick={complete}
                        disabled={busy || blockers.length > 0}
                        data-testid="ws3-complete"
                        className="text-[13px] px-3 py-1.5 bg-charcoal text-white rounded-md disabled:opacity-40">
                        Complete Workstage 3
                    </button>
                </div>
            </section>
            {/*
             * Upload Signed Agreement modal.  Two endpoint shapes:
             *   • `!agr`         → new atomic `/upload-signed` route
             *                       (creates agreement + executes in one call)
             *   • `agr` present  → legacy `/{aid}/record-external-execution`
             *                       (for rows that reached a pre-execution
             *                       status under the retired generation flow;
             *                       preserved for forward compatibility, no
             *                       live rows should exist in this state
             *                       post-retcon)
             */}
            <RecordExternalExecutionModal
                open={recExtOpen}
                onClose={() => setRecExtOpen(false)}
                endpointUrl={agr
                    ? `${API}/admin/projects/${projectId}/ws3/agreement/${agr.id}/record-external-execution`
                    : `${API}/admin/projects/${projectId}/ws3/agreement/upload-signed`}
                submitLabel="Upload Signed Agreement"
                helpText={"Attach the externally-signed Free Design Agreement PDF and capture structured signer evidence. "
                          + "The uploaded PDF becomes the authoritative executed document."}
                testIdPrefix="ws3-rec-ext-exec"
                projectId={projectId}
                onSuccessData={async () => {
                    toast.success("Signed agreement uploaded and executed.");
                    await load();
                }} />
        </div>
        </fieldset>
    );
}

// Design Brief drawer removed in Phase 2 of the legacy
// auto-proposal-system removal.  The brief had no downstream consumer
// post-retirement of the generation flow, so its capture surface,
// backend endpoints, collection, and this UI module were deleted.

export default WS3ActiveModules;

